"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateNetlist = validateNetlist;
exports.findModelModulesAbsentFromNetlist = findModelModulesAbsentFromNetlist;
exports.getOracleRejectedRowLineNumbers = getOracleRejectedRowLineNumbers;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const types_1 = require("./types");
const pcadNetlistParser_1 = require("../Parse/PCAD/pcadNetlistParser");
const PCAD_MAX_NET_NAME_LENGTH = 14;
function isPcadTruncationPlaceholder(netName) {
    return /^NET\d{5}$/.test(netName);
}
function normalizePinRef(pinRef) {
    return pinRef.replace(/,\s*/g, ',').replace(/ /g, '_');
}
function findDuplicatePinGroup(connection, moduleMapping, duplicatePinCatalog) {
    const physicalModuleName = moduleMapping.get(connection.moduleName) ?? connection.moduleName;
    const groupsByModuleRef = duplicatePinCatalog.get(physicalModuleName);
    if (!groupsByModuleRef) {
        return undefined;
    }
    const candidateGroups = connection.moduleRef !== undefined ?
        [groupsByModuleRef.get(connection.moduleRef) ?? new Map()]
        : [...groupsByModuleRef.values()];
    for (const groupsByNumber of candidateGroups) {
        for (const group of groupsByNumber.values()) {
            if (group.some((pin) => pin.ref === connection.pinRef)) {
                return group;
            }
        }
    }
    return undefined;
}
function getComparablePinRef(connection, moduleMapping, duplicatePinCatalog) {
    const group = findDuplicatePinGroup(connection, moduleMapping, duplicatePinCatalog);
    return group?.find((pin) => pin.ref === connection.pinRef)?.number ?? connection.pinRef;
}
function isRepeatedModelPinNumber(connection, moduleMapping, duplicatePinCatalog) {
    const physicalModuleName = moduleMapping.get(connection.moduleName) ?? connection.moduleName;
    const groupsByModelModuleName = duplicatePinCatalog.get(physicalModuleName);
    if (!groupsByModelModuleName) {
        return false;
    }
    return [...groupsByModelModuleName.values()].some((groupsByNumber) => groupsByNumber.has(connection.pinRef));
}
function connectionsMatch(projectConnection, netlistConnection, moduleMapping, duplicatePinCatalog) {
    const group = findDuplicatePinGroup(projectConnection, moduleMapping, duplicatePinCatalog);
    const projectPin = group?.find((pin) => pin.ref === projectConnection.pinRef);
    if (!group || !projectPin) {
        return pinRefsMatch(projectConnection.pinRef, netlistConnection.pinRef);
    }
    if (!pinRefsMatch(projectPin.number, netlistConnection.pinRef)) {
        return false;
    }
    const pinFunction = netlistConnection.pinFunction;
    if (!pinFunction || !projectPin.name || projectPin.name === '~' || projectPin.name === pinFunction) {
        return true;
    }
    const functionPin = group.find((pin) => pin.name === pinFunction);
    return functionPin?.netName === projectPin.netName;
}
function buildDuplicatePinCatalog(projectModel, moduleMapping) {
    const catalog = new Map();
    if (projectModel.type !== 'kicad') {
        return catalog;
    }
    for (const design of Object.values(projectModel.designs)) {
        if (design.modelType !== 'schematic') {
            continue;
        }
        for (const module of design.modules) {
            const component = projectModel.components[module.componentRef];
            const symbol = component?.symbols.find((candidate) => candidate.name === module.footprintRef);
            if (!symbol) {
                continue;
            }
            const pinsByNumber = new Map();
            for (const pin of symbol.pins) {
                const pins = pinsByNumber.get(pin.number) ?? [];
                pins.push(pin);
                pinsByNumber.set(pin.number, pins);
            }
            const repeatedPins = [...pinsByNumber.entries()].filter(([, pins]) => pins.length > 1);
            if (repeatedPins.length === 0) {
                continue;
            }
            const physicalModuleName = moduleMapping.get(module.name) ?? module.name;
            const groupsByModuleRef = catalog.get(physicalModuleName) ?? new Map();
            const groupsByNumber = groupsByModuleRef.get(module.ref) ?? new Map();
            for (const [number, pins] of repeatedPins) {
                groupsByNumber.set(number, pins.map((pin) => ({
                    ref: pin.ref,
                    number: pin.number,
                    name: pin.name,
                    netName: module.pinNets[pin.ref],
                })));
            }
            groupsByModuleRef.set(module.ref, groupsByNumber);
            catalog.set(physicalModuleName, groupsByModuleRef);
        }
    }
    return catalog;
}
function pinRefsMatch(pinRef1, pinRef2) {
    if (pinRef1 === pinRef2) {
        return true;
    }
    return normalizePinRef(pinRef1) === normalizePinRef(pinRef2);
}
function findNetlistConnection(pinRef, netlistModule) {
    if (netlistModule.pinNets[pinRef] !== undefined) {
        return netlistModule.pinNets[pinRef];
    }
    for (const [netlistPinRef, netName] of Object.entries(netlistModule.pinNets)) {
        if (pinRefsMatch(pinRef, netlistPinRef)) {
            return netName;
        }
    }
    return undefined;
}
function findProjectConnection(pinRef, designModule) {
    if (designModule.pinNets[pinRef] !== undefined) {
        return designModule.pinNets[pinRef];
    }
    for (const [projectPinRef, netName] of Object.entries(designModule.pinNets)) {
        if (pinRefsMatch(pinRef, projectPinRef)) {
            return netName;
        }
    }
    return undefined;
}
function buildConnectionSignature(connections, pinRefResolver) {
    const parts = new Set();
    for (const conn of connections) {
        parts.add(`${conn.moduleName}\0${normalizePinRef(pinRefResolver?.(conn) ?? conn.pinRef)}`);
    }
    return [...parts].sort().join('\x01');
}
function buildRealConnectionIndex(nets, moduleMapping, virtualModuleNames, pinRefResolver) {
    const index = new Map();
    for (const [netName, netInfo] of nets) {
        const realConnections = netInfo.connections.flatMap((connection) => {
            const moduleName = moduleMapping.get(connection.moduleName) ?? connection.moduleName;
            return virtualModuleNames.has(moduleName) ?
                []
                : [{ moduleName, moduleRef: connection.moduleRef, pinRef: connection.pinRef }];
        });
        if (realConnections.length === 0) {
            continue;
        }
        const signature = buildConnectionSignature(realConnections, pinRefResolver);
        const matches = index.get(signature) ?? [];
        matches.push([netName, netInfo]);
        index.set(signature, matches);
    }
    return index;
}
function normalizeNetName(name, options) {
    let n = name;
    if (options.ignoreWhitespace) {
        n = n.replace(/\s+/g, '');
    }
    if (options.ignoreCase) {
        n = n.toLowerCase();
    }
    return n;
}
function buildNormalizedNetIndex(nets, options) {
    const index = new Map();
    if (!options.ignoreCase && !options.ignoreWhitespace) {
        return index;
    }
    for (const [netName, netInfo] of nets) {
        const normalized = normalizeNetName(netName, options);
        if (!index.has(normalized)) {
            index.set(normalized, [netName, netInfo]);
        }
    }
    return index;
}
function findProjectNetForOracleConnection(oracleConnection, projectConnectionIndex) {
    return projectConnectionIndex.get(buildConnectionSignature([oracleConnection]))?.[1];
}
function buildReferenceModulesByName(modules) {
    const modulesByName = new Map();
    for (const module of modules) {
        const existingModule = modulesByName.get(module.name);
        if (!existingModule) {
            modulesByName.set(module.name, { ...module, pinNets: { ...module.pinNets } });
            continue;
        }
        for (const [pinRef, netName] of Object.entries(module.pinNets)) {
            if (netName !== types_1.NO_CONNECT_PLACEHOLDER_NET_NAME || existingModule.pinNets[pinRef] === undefined) {
                existingModule.pinNets[pinRef] = netName;
            }
        }
        modulesByName.set(module.name, { ...module, pinNets: existingModule.pinNets });
    }
    return modulesByName;
}
function getEffectiveNoConnectDeclarations(declarations, realNets) {
    if (!declarations) {
        return undefined;
    }
    const connectedPinKeys = new Set();
    for (const net of realNets.values()) {
        for (const connection of net.connections) {
            connectedPinKeys.add(buildConnectionSignature([connection]));
        }
    }
    return {
        ...declarations,
        connections: declarations.connections.filter((declaration) => !connectedPinKeys.has(buildConnectionSignature([declaration]))),
    };
}
function validateNetlist(projectModel, netlistSchematic, options = {}) {
    const errors = [];
    const opts = {
        ignoreCase: false,
        ignoreWhitespace: false,
        ...options,
    };
    const modulesByName = new Map();
    for (const mod of projectModel.modules) {
        modulesByName.set(mod.name, mod);
    }
    const netlistModulesByName = buildReferenceModulesByName(netlistSchematic.modules);
    const virtualModuleNames = new Set();
    for (const mod of projectModel.modules) {
        const component = projectModel.components[mod.componentRef];
        if (component?.isVirtual === true) {
            virtualModuleNames.add(mod.name);
        }
    }
    const moduleMapping = buildModuleMapping(projectModel);
    const projectNets = new Map();
    for (const design of Object.values(projectModel.designs)) {
        if (design.modelType !== 'schematic') {
            continue;
        }
        for (const [netName, netInfo] of Object.entries(design.nets)) {
            if (netInfo.metadata?.[sigma_engine_shared_1.VIRTUAL_NET_METADATA_KEY] === sigma_engine_shared_1.NO_CONNECT_VIRTUAL_NET_METADATA_VALUE) {
                continue;
            }
            const isAutoNamed = netName.includes('__UNNAMED_NET_');
            const key = isAutoNamed ? `${design.designId}\0${netName}` : netName;
            const existingNet = projectNets.get(key);
            if (existingNet) {
                const seen = new Set();
                for (const conn of existingNet.connections) {
                    seen.add(`${conn.moduleRef}\0${conn.pinRef}`);
                }
                const combinedConnections = [...existingNet.connections];
                for (const newConn of netInfo.connections) {
                    const netKey = `${newConn.moduleRef}\0${newConn.pinRef}`;
                    if (!seen.has(netKey)) {
                        seen.add(netKey);
                        combinedConnections.push(newConn);
                    }
                }
                projectNets.set(key, {
                    ...existingNet,
                    connections: combinedConnections,
                });
            }
            else {
                projectNets.set(key, netInfo);
            }
        }
    }
    const duplicatePinCatalog = buildDuplicatePinCatalog(projectModel, moduleMapping);
    const netlistNets = new Map();
    const rawPcadConnections = (0, pcadNetlistParser_1.getPcadNetlistRawConnections)(netlistSchematic);
    const rejectedOracleRows = (0, pcadNetlistParser_1.getPcadRejectedConnectionRows)(netlistSchematic) ?? [];
    const positiveAssertionsOnly = (0, pcadNetlistParser_1.usesPcadPositiveAssertionsOnly)(netlistSchematic);
    const isPcadNetlist = netlistSchematic.sourceFile === 'pcad-netlist';
    if (rawPcadConnections) {
        for (const { moduleName, pinRef, netName } of rawPcadConnections) {
            if (positiveAssertionsOnly && netName === types_1.NO_CONNECT_PLACEHOLDER_NET_NAME) {
                continue;
            }
            const net = netlistNets.get(netName) ?? { name: netName, connections: [] };
            net.connections.push({ moduleRef: moduleName, moduleName, pinRef });
            netlistNets.set(netName, net);
        }
    }
    else {
        for (const [netName, netInfo] of Object.entries(netlistSchematic.nets)) {
            netlistNets.set(netName, netInfo);
        }
    }
    const rawNoConnectDeclarations = positiveAssertionsOnly ? undefined : netlistNets.get(types_1.NO_CONNECT_PLACEHOLDER_NET_NAME);
    netlistNets.delete(types_1.NO_CONNECT_PLACEHOLDER_NET_NAME);
    const noConnectDeclarations = getEffectiveNoConnectDeclarations(rawNoConnectDeclarations, netlistNets);
    const oracleUnknownConnections = positiveAssertionsOnly ?
        collectUnknownOracleConnections(projectNets, netlistNets, netlistModulesByName, moduleMapping, virtualModuleNames, duplicatePinCatalog)
        : [];
    const projectConnectionIndex = new Map();
    for (const [projectNetName, projectNet] of projectNets) {
        for (const connection of projectNet.connections) {
            const oracleModuleName = moduleMapping.get(connection.moduleName) ?? connection.moduleName;
            projectConnectionIndex.set(buildConnectionSignature([{ moduleName: oracleModuleName, pinRef: connection.pinRef }], () => getComparablePinRef(connection, moduleMapping, duplicatePinCatalog)), [projectNetName, projectNet]);
        }
    }
    const projectNormalizedIndex = buildNormalizedNetIndex(projectNets, opts);
    const netlistNormalizedIndex = buildNormalizedNetIndex(netlistNets, opts);
    const fullyNonCanonicalDuplicateNetNames = new Set();
    for (const [netName, netInfo] of netlistNets) {
        const hasRepeatedModelPinNumber = netInfo.connections.some((connection) => isRepeatedModelPinNumber(connection, moduleMapping, duplicatePinCatalog));
        if (!hasRepeatedModelPinNumber && isFullyNonCanonicalDuplicateNet(netName, netInfo, netlistModulesByName)) {
            fullyNonCanonicalDuplicateNetNames.add(netName);
        }
    }
    const unmatchedProjectNets = [...projectNets].filter(([netName]) => !findMatchingNet(netName, netlistNets, opts, netlistNormalizedIndex));
    const unmatchedNetlistNets = [...netlistNets].filter(([netName]) => !findMatchingNet(netName, projectNets, opts, projectNormalizedIndex) &&
        !fullyNonCanonicalDuplicateNetNames.has(netName));
    const unmatchedProjectByRealConnections = buildRealConnectionIndex(unmatchedProjectNets, moduleMapping, virtualModuleNames, (connection) => getComparablePinRef(connection, moduleMapping, duplicatePinCatalog));
    const oracleModuleMapping = new Map();
    const oracleVirtualModuleNames = new Set();
    const unmatchedNetlistByRealConnections = buildRealConnectionIndex(unmatchedNetlistNets, oracleModuleMapping, oracleVirtualModuleNames);
    const pairedProjectNetsByNetlistName = new Map();
    for (const [signature, netlistMatches] of unmatchedNetlistByRealConnections) {
        const projectMatches = unmatchedProjectByRealConnections.get(signature);
        if (netlistMatches.length !== 1 || projectMatches?.length !== 1) {
            continue;
        }
        const [netlistNetName] = netlistMatches[0];
        const projectMatch = projectMatches[0];
        pairedProjectNetsByNetlistName.set(netlistNetName, projectMatch);
    }
    const pairedProjectNetNames = new Set([...pairedProjectNetsByNetlistName.values()].map(([projectNetName]) => projectNetName));
    let totalNetsChecked = 0;
    let netNameMismatches = 0;
    let missingConnections = 0;
    let missingNets = 0;
    let unexpectedNets = 0;
    if (noConnectDeclarations) {
        const declaredConnectionKeys = new Set(noConnectDeclarations.connections.map((declaration) => buildConnectionSignature([declaration])));
        for (const declaration of noConnectDeclarations.connections) {
            const projectNet = findProjectNetForOracleConnection(declaration, projectConnectionIndex);
            if (projectNet) {
                errors.push({
                    type: types_1.NetlistValidationErrorType.MissingModuleConnection,
                    message: `Netlist declares module '${declaration.moduleName}' pin '${declaration.pinRef}' as no-connect, but project model connects it to net '${projectNet.name}'`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    projectNetName: projectNet.name,
                    netlistNetName: types_1.NO_CONNECT_PLACEHOLDER_NET_NAME,
                    moduleName: declaration.moduleName,
                    pinRef: declaration.pinRef,
                });
                missingConnections++;
                continue;
            }
        }
        for (const [projectNetName, projectNet] of projectNets) {
            if (projectNet.connections.length > 0 &&
                projectNet.connections.every((connection) => declaredConnectionKeys.has(buildConnectionSignature([
                    {
                        moduleName: moduleMapping.get(connection.moduleName) ?? connection.moduleName,
                        pinRef: connection.pinRef,
                    },
                ])))) {
                projectNets.delete(projectNetName);
            }
        }
    }
    for (const [netlistNetName, netlistNet] of netlistNets) {
        totalNetsChecked++;
        if (fullyNonCanonicalDuplicateNetNames.has(netlistNetName)) {
            continue;
        }
        let matchingProjectNet = findMatchingNet(netlistNetName, projectNets, opts, projectNormalizedIndex);
        const matchedByName = matchingProjectNet !== null;
        if (!matchingProjectNet) {
            matchingProjectNet = pairedProjectNetsByNetlistName.get(netlistNetName) ?? null;
        }
        if (!matchingProjectNet) {
            errors.push({
                type: types_1.NetlistValidationErrorType.UnexpectedNetFound,
                message: `Net '${netlistNetName}' found in netlist but not defined in project model`,
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                netlistNetName,
                actualConnections: netlistNet.connections.map((conn) => ({
                    moduleName: conn.moduleName,
                    pinRef: conn.pinRef,
                    ...(conn.pinFunction ? { pinFunction: conn.pinFunction } : {}),
                })),
            });
            unexpectedNets++;
            continue;
        }
        const [projectNetName, projectNet] = matchingProjectNet;
        const isPcadLengthPlaceholder = isPcadNetlist &&
            !matchedByName &&
            isPcadTruncationPlaceholder(netlistNetName) &&
            projectNetName.length > PCAD_MAX_NET_NAME_LENGTH;
        if (netlistNetName !== projectNetName && !isPcadLengthPlaceholder) {
            errors.push({
                type: types_1.NetlistValidationErrorType.NetNameMismatch,
                message: `Net name mismatch: netlist has '${netlistNetName}', project model expects '${projectNetName}'`,
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                projectNetName,
                netlistNetName,
                connections: projectNet.connections.map((conn) => ({
                    moduleName: conn.moduleName,
                    pinRef: conn.pinRef,
                })),
                actualConnections: netlistNet.connections.map((conn) => ({
                    moduleName: conn.moduleName,
                    pinRef: conn.pinRef,
                    ...(conn.pinFunction ? { pinFunction: conn.pinFunction } : {}),
                })),
            });
            netNameMismatches++;
        }
        const connectionErrors = validateProjectModelConnections(netlistNetName, netlistNet, projectNet, moduleMapping, virtualModuleNames, modulesByName, netlistModulesByName, projectModel, duplicatePinCatalog);
        errors.push(...connectionErrors);
        missingConnections += connectionErrors.length;
    }
    for (const [projectNetName, projectNet] of projectNets) {
        const hasMatchingNetlistNet = Boolean(findMatchingNet(projectNetName, netlistNets, opts, netlistNormalizedIndex) ||
            pairedProjectNetNames.has(projectNetName));
        if (!hasMatchingNetlistNet) {
            const allConnectionsToVirtualModules = projectNet.connections.every((connection) => virtualModuleNames.has(moduleMapping.get(connection.moduleName) ?? connection.moduleName));
            if (allConnectionsToVirtualModules) {
                continue;
            }
            errors.push({
                type: types_1.NetlistValidationErrorType.UnexpectedProjectModelNet,
                message: `Net '${projectNetName}' defined in project model but missing from netlist`,
                severity: projectNet.connections.length > 1 ? sigma_engine_shared_1.BoundaryLevel.Error : sigma_engine_shared_1.BoundaryLevel.Recommendation,
                projectNetName,
                connections: projectNet.connections.map((conn) => ({
                    moduleName: conn.moduleName,
                    pinRef: conn.pinRef,
                })),
            });
            missingNets++;
        }
    }
    const result = {
        type: 'NetlistValidation',
        message: `Netlist validation completed with ${errors.length} issues found`,
        severity: errors.length > 0 ? sigma_engine_shared_1.BoundaryLevel.Warning : sigma_engine_shared_1.BoundaryLevel.Recommendation,
        errors,
        summary: {
            totalNetsChecked,
            totalErrorsFound: errors.length,
            netNameMismatches,
            missingConnections,
            missingNets,
            unexpectedNets,
            missingModules: errors.filter((e) => e.type === types_1.NetlistValidationErrorType.MissingModule).length,
            oracleRejectedRows: { lineNumbers: rejectedOracleRows.map((row) => row.lineNumber) },
            ...(positiveAssertionsOnly ? { oracleUnknownConnections: { connections: oracleUnknownConnections } } : {}),
            unknownDesignators: summarizeUnknownDesignators(netlistNets, netlistModulesByName, collectModelDesignators(modulesByName, moduleMapping, projectNets)),
        },
    };
    return result;
}
function findModelModulesAbsentFromNetlist(projectModel, netlistSchematic) {
    const netlistDesignators = new Set(netlistSchematic.modules.map((mod) => mod.name));
    const rawPcadConnections = (0, pcadNetlistParser_1.getPcadNetlistRawConnections)(netlistSchematic);
    if (rawPcadConnections) {
        for (const { moduleName } of rawPcadConnections) {
            netlistDesignators.add(moduleName);
        }
    }
    else {
        for (const net of Object.values(netlistSchematic.nets)) {
            for (const connection of net.connections) {
                netlistDesignators.add(connection.moduleName);
            }
        }
    }
    const symbolNamesByModuleName = new Map();
    for (const [symbolName, moduleName] of buildModuleMapping(projectModel)) {
        symbolNamesByModuleName.set(moduleName, [...(symbolNamesByModuleName.get(moduleName) ?? []), symbolName]);
    }
    const absent = [];
    for (const mod of projectModel.modules) {
        if (projectModel.components[mod.componentRef]?.isVirtual === true) {
            continue;
        }
        const names = [mod.name, ...(symbolNamesByModuleName.get(mod.name) ?? [])];
        if (!names.some((name) => netlistDesignators.has(name))) {
            absent.push(mod.name);
        }
    }
    return absent.sort();
}
function collectModelDesignators(modulesByName, moduleMapping, projectNets) {
    const designators = new Set([...modulesByName.keys(), ...moduleMapping.keys()]);
    for (const net of projectNets.values()) {
        for (const connection of net.connections) {
            designators.add(connection.moduleName);
        }
    }
    return designators;
}
function summarizeUnknownDesignators(netlistNets, netlistModulesByName, modelDesignators) {
    const unknown = new Set();
    for (const name of netlistModulesByName.keys()) {
        if (!modelDesignators.has(name)) {
            unknown.add(name);
        }
    }
    for (const net of netlistNets.values()) {
        for (const connection of net.connections) {
            if (!modelDesignators.has(connection.moduleName)) {
                unknown.add(connection.moduleName);
            }
        }
    }
    const modelBases = new Set();
    for (const name of modelDesignators) {
        modelBases.add(designatorBase(name));
    }
    let matchedInOtherForm = 0;
    for (const designator of unknown) {
        if (modelBases.has(designatorBase(designator))) {
            matchedInOtherForm++;
        }
    }
    return { count: unknown.size, matchedInOtherForm };
}
function designatorBase(designator) {
    return /^(.*?\d)[._]/.exec(designator)?.[1] ?? designator;
}
function collectUnknownOracleConnections(projectNets, netlistNets, netlistModulesByName, moduleMapping, virtualModuleNames, duplicatePinCatalog) {
    const positiveKeys = new Set();
    for (const net of netlistNets.values()) {
        for (const connection of net.connections) {
            positiveKeys.add(buildConnectionSignature([connection]));
        }
    }
    const unknown = new Map();
    for (const net of projectNets.values()) {
        for (const connection of net.connections) {
            const moduleName = moduleMapping.get(connection.moduleName) ?? connection.moduleName;
            if (virtualModuleNames.has(moduleName)) {
                continue;
            }
            if (!netlistModulesByName.has(moduleName)) {
                continue;
            }
            const candidate = { moduleName, pinRef: connection.pinRef };
            const key = buildConnectionSignature([candidate], () => getComparablePinRef(connection, moduleMapping, duplicatePinCatalog));
            if (!positiveKeys.has(key)) {
                unknown.set(key, candidate);
            }
        }
    }
    return [...unknown.values()].sort((left, right) => {
        const moduleOrder = left.moduleName.localeCompare(right.moduleName);
        return moduleOrder !== 0 ? moduleOrder : left.pinRef.localeCompare(right.pinRef);
    });
}
function getOracleRejectedRowLineNumbers(result) {
    return result.summary.oracleRejectedRows?.lineNumbers ?? [];
}
function findMatchingNet(netName, targetNets, options, normalizedIndex) {
    const exactMatch = targetNets.get(netName);
    if (exactMatch) {
        return [netName, exactMatch];
    }
    if (options.ignoreCase || options.ignoreWhitespace) {
        const normalized = normalizeNetName(netName, options);
        const fuzzyMatch = normalizedIndex.get(normalized);
        if (fuzzyMatch) {
            return fuzzyMatch;
        }
    }
    return null;
}
function isFullyNonCanonicalDuplicateNet(netlistNetName, netlistNet, netlistModulesByName) {
    if (netlistNet.connections.length === 0) {
        return false;
    }
    return netlistNet.connections.every((connection) => {
        const module = netlistModulesByName.get(connection.moduleName);
        const canonicalNetName = module ? findNetlistConnection(connection.pinRef, module) : undefined;
        return canonicalNetName !== undefined && canonicalNetName !== netlistNetName;
    });
}
function validateProjectModelConnections(netlistNetName, netlistNet, projectNet, moduleMapping, virtualModuleNames, modulesByName, netlistModulesByName, projectModel, duplicatePinCatalog) {
    const errors = [];
    const hasRepeatedModelPinNumber = netlistNet.connections.some((connection) => isRepeatedModelPinNumber(connection, moduleMapping, duplicatePinCatalog));
    const isNonCanonicalDuplicateNet = !hasRepeatedModelPinNumber &&
        netlistNet.connections.some((connection) => {
            const module = netlistModulesByName.get(connection.moduleName);
            const canonicalNetName = module ? findNetlistConnection(connection.pinRef, module) : undefined;
            return canonicalNetName !== undefined && canonicalNetName !== netlistNetName;
        });
    for (const expectedConn of projectNet.connections) {
        const physicalModuleName = moduleMapping.get(expectedConn.moduleName) ?? expectedConn.moduleName;
        if (virtualModuleNames.has(physicalModuleName)) {
            continue;
        }
        let connectionExistsInNetlist = false;
        for (const c of netlistNet.connections) {
            if ((c.moduleName === expectedConn.moduleName || c.moduleName === physicalModuleName) &&
                connectionsMatch(expectedConn, c, moduleMapping, duplicatePinCatalog)) {
                connectionExistsInNetlist = true;
                break;
            }
        }
        if (!connectionExistsInNetlist && !isNonCanonicalDuplicateNet) {
            const netlistModule = netlistModulesByName.get(expectedConn.moduleName);
            const netlistConnection = netlistModule ? findNetlistConnection(expectedConn.pinRef, netlistModule) : undefined;
            errors.push({
                type: types_1.NetlistValidationErrorType.MissingModuleConnection,
                message: `Project model has module '${expectedConn.moduleName}' pin '${getComparablePinRef(expectedConn, moduleMapping, duplicatePinCatalog)}' connected to net '${netlistNetName}', but netlist shows it connected to net '${netlistConnection}'`,
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
                projectNetName: projectNet.name,
                netlistNetName: netlistConnection,
                moduleName: expectedConn.moduleName,
                pinRef: getComparablePinRef(expectedConn, moduleMapping, duplicatePinCatalog),
            });
        }
    }
    for (const actualConn of netlistNet.connections) {
        if (virtualModuleNames.has(actualConn.moduleName)) {
            continue;
        }
        let connectionExpectedByProject = false;
        for (const c of projectNet.connections) {
            if (virtualModuleNames.has(c.moduleName)) {
                continue;
            }
            const physicalModuleName = moduleMapping.get(c.moduleName) ?? c.moduleName;
            if ((c.moduleName === actualConn.moduleName || physicalModuleName === actualConn.moduleName) &&
                connectionsMatch(c, actualConn, moduleMapping, duplicatePinCatalog)) {
                connectionExpectedByProject = true;
                break;
            }
        }
        if (!connectionExpectedByProject) {
            const projectModule = modulesByName.get(actualConn.moduleName);
            if (!projectModule) {
                errors.push({
                    type: types_1.NetlistValidationErrorType.MissingModule,
                    message: `Module '${actualConn.moduleName}' not found in project model`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                    netlistNetName,
                    moduleName: actualConn.moduleName,
                    pinRef: actualConn.pinRef,
                });
                continue;
            }
            let connection;
            for (const instance of projectModule.instances) {
                if (projectModel.designs[instance.designId].modelType !== 'schematic') {
                    continue;
                }
                const design = projectModel.designs[instance.designId];
                let designModule;
                for (const m of design.modules) {
                    if (m.ref === instance.ref) {
                        designModule = m;
                        break;
                    }
                }
                if (designModule && findProjectConnection(actualConn.pinRef, designModule) !== undefined) {
                    connection = instance;
                    break;
                }
            }
            if (!connection) {
                errors.push({
                    type: types_1.NetlistValidationErrorType.UnexpectedConnection,
                    message: `Netlist shows module '${actualConn.moduleName}' pin '${actualConn.pinRef}' connected to net '${netlistNetName}', but project model has it unconnected`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                    netlistNetName,
                    moduleName: actualConn.moduleName,
                    pinRef: actualConn.pinRef,
                });
                continue;
            }
            const design = projectModel.designs[connection.designId];
            let designModule;
            for (const m of design.modules) {
                if (m.ref === connection.ref) {
                    designModule = m;
                    break;
                }
            }
            if (!designModule) {
                throw new Error(`Module ${connection.ref} not found in design ${connection.designId}`);
            }
            const projectConnection = findProjectConnection(actualConn.pinRef, designModule);
            errors.push({
                type: types_1.NetlistValidationErrorType.UnexpectedConnection,
                message: `Netlist shows module '${actualConn.moduleName}' pin '${actualConn.pinRef}' connected to net '${netlistNetName}', but project model has it connected to net '${projectConnection}'`,
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                projectNetName: projectConnection,
                netlistNetName,
                moduleName: actualConn.moduleName,
                pinRef: actualConn.pinRef,
            });
        }
    }
    return errors;
}
function buildModuleMapping(projectModel) {
    const refToModuleName = new Map();
    const moduleNameToPhysicalName = new Map();
    const sourceReferenceModuleNames = new Set();
    for (const design of Object.values(projectModel.designs)) {
        if (design.modelType === 'schematic') {
            for (const module of design.modules) {
                refToModuleName.set(module.ref, module.name);
                const sourceReference = module.metadata?.['kicad.sourceReference'];
                if (typeof sourceReference === 'string' &&
                    sourceReference.length > 0 &&
                    module.name.startsWith(sourceReference) &&
                    module.name.length > sourceReference.length) {
                    moduleNameToPhysicalName.set(module.name, sourceReference);
                    sourceReferenceModuleNames.add(module.name);
                }
            }
        }
    }
    for (const projectModule of projectModel.modules) {
        for (const instance of projectModule.instances) {
            const schematicSymbolName = refToModuleName.get(instance.ref);
            if (schematicSymbolName && !sourceReferenceModuleNames.has(schematicSymbolName)) {
                moduleNameToPhysicalName.set(schematicSymbolName, projectModule.name);
            }
        }
    }
    return moduleNameToPhysicalName;
}
//# sourceMappingURL=netlistValidation.js.map